/**
 * portfolioTickersCache — Round P141·6 (Feb 2026)
 *
 * Shared, request-deduped cache of the current user's watchlist
 * ticker set. Every ★ button on the page (441 rows on the Warrant
 * Screener alone) previously fired its OWN `GET /api/portfolio`
 * on mount — the browser caps concurrent requests at ~6 per origin,
 * so stars trickled in over 20+ seconds as the queue drained.
 *
 * With this cache, all star buttons share ONE in-flight promise and
 * paint immediately from a Set. Mutations (add / remove) update the
 * cache locally and broadcast `watchlist-changed` for any consumer
 * that needs to re-hydrate.
 *
 * Contract:
 *   getTickers()  → Promise<Set<string>>  (fetches if not loaded)
 *   subscribe(fn) → unsubscribe
 *   addLocal(t)   → optimistic add
 *   removeLocal(t)→ optimistic remove
 *   invalidate()  → force next getTickers() to refetch
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let tickerSet = null;           // Set<string> | null (null = not loaded)
let inflight = null;            // Promise<Set> | null
const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(); } catch { /* noop */ }
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Returns the cached ticker set immediately if loaded, otherwise
 * fires a single GET /api/portfolio and returns a Set on resolve.
 * Concurrent callers share the in-flight promise.
 */
export async function getTickers() {
  if (tickerSet) return tickerSet;
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/portfolio`, { withCredentials: true })
    .then((r) => {
      tickerSet = new Set((r.data || []).map((p) => p.ticker));
      inflight = null;
      emit();
      return tickerSet;
    })
    .catch(() => {
      // 401 / network error — set an empty cache so callers can
      // render immediately. `invalidate()` will force a retry on
      // the next star-toggle click.
      tickerSet = new Set();
      inflight = null;
      emit();
      return tickerSet;
    });
  return inflight;
}

/**
 * Synchronous getter — returns the cached set (or null if not yet
 * loaded). Callers should still `getTickers()` on mount to trigger
 * the hydrate; this getter is for immediate first-paint checks.
 */
export function peekTickers() {
  return tickerSet;
}

/**
 * Optimistically add a ticker locally (on POST success). Emits so
 * every subscribed star repaints.
 */
export function addLocal(ticker) {
  if (!tickerSet) tickerSet = new Set();
  tickerSet.add(ticker);
  emit();
}

/**
 * Optimistically remove a ticker locally (on DELETE success). Emits
 * so every subscribed star repaints.
 */
export function removeLocal(ticker) {
  if (!tickerSet) return;
  tickerSet.delete(ticker);
  emit();
}

/**
 * Nuke the cache so the next `getTickers()` refetches. Useful when
 * an external event (e.g. bulk CSV import) may have changed the
 * watchlist server-side outside our knowledge.
 */
export function invalidate() {
  tickerSet = null;
  inflight = null;
  emit();
}

// Listen to global watchlist-changed events so other UI paths (e.g.
// Dashboard's built-in add/remove flow) can broadcast a change and we
// pick it up. Refresh in the background but KEEP the current set while
// the fetch is in flight — nulling it here meant the next addLocal()
// rebuilt a one-ticker set and every other star on the page went hollow.
try {
  window.addEventListener('watchlist-changed', () => {
    if (inflight) return;
    inflight = axios
      .get(`${API}/portfolio`, { withCredentials: true })
      .then((r) => {
        tickerSet = new Set((r.data || []).map((p) => p.ticker));
        inflight = null;
        emit();
        return tickerSet;
      })
      .catch(() => {
        inflight = null;
        return tickerSet || new Set();
      });
  });
} catch { /* SSR safety */ }
